import { useMemo, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatQty } from '../lib/format.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

interface PackingLine {
  id: string;
  qty: number;
  label: string;
}

export function OrderPalletPackingDialog({
  orderId,
  lines,
  onClose,
  onPacked,
}: {
  orderId: string;
  lines: PackingLine[];
  onClose: () => void;
  onPacked: () => void;
}) {
  const [palletCount, setPalletCount] = useState(1);
  const [allocations, setAllocations] = useState<Record<string, string>[]>([
    Object.fromEntries(lines.map((line) => [line.id, String(line.qty)])),
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function changePalletCount(raw: number) {
    const nextCount = Math.max(1, Math.min(50, Math.trunc(raw || 1)));
    setPalletCount(nextCount);
    setAllocations((current) =>
      Array.from({ length: nextCount }, (_, index) => current[index] ?? {}),
    );
  }

  function setQty(palletIndex: number, lineId: string, value: string) {
    setAllocations((current) =>
      current.map((pallet, index) =>
        index === palletIndex ? { ...pallet, [lineId]: value } : pallet,
      ),
    );
  }

  const totals = useMemo(
    () =>
      new Map(
        lines.map((line) => [
          line.id,
          allocations.reduce((sum, pallet) => sum + Number(pallet[line.id] || 0), 0),
        ]),
      ),
    [allocations, lines],
  );
  const exact = lines.every((line) => Math.abs((totals.get(line.id) ?? 0) - line.qty) < 0.000_001);
  const everyPalletUsed = allocations.every((pallet) =>
    lines.some((line) => Number(pallet[line.id] || 0) > 0),
  );

  async function submit() {
    if (!exact || !everyPalletUsed) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/orders/${orderId}/pack-pallets`, {
        method: 'POST',
        body: {
          pallets: allocations.map((pallet) => ({
            lines: lines
              .map((line) => ({ orderLineId: line.id, qty: Number(pallet[line.id] || 0) }))
              .filter((line) => line.qty > 0),
          })),
        },
      });
      onPacked();
    } catch {
      setError(t('order.pack.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('order.pack.title')} onClose={onClose} size="wide" height="tall">
      <div className="space-y-4 text-sm">
        <p className="text-xs text-muted-foreground">{t('order.pack.hint')}</p>
        <label className="flex items-center gap-3">
          <span className="font-medium">{t('order.pack.pallet_count')}</span>
          <input
            type="number"
            min={1}
            max={50}
            step={1}
            value={palletCount}
            onChange={(event) => changePalletCount(Number(event.target.value))}
            className="w-24 rounded border border-border bg-background px-2 py-1.5 text-right tabular-nums"
          />
        </label>
        <div className="overflow-x-auto rounded border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('order.pack.line')}</th>
                {allocations.map((_, index) => (
                  <th key={index} className="px-3 py-2 text-right font-medium">
                    {t('order.pack.pallet')} {index + 1}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-medium">{t('order.pack.total')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const total = totals.get(line.id) ?? 0;
                const matches = Math.abs(total - line.qty) < 0.000_001;
                return (
                  <tr key={line.id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <div>{line.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {t('order.pack.required')}: {formatQty(line.qty)}
                      </div>
                    </td>
                    {allocations.map((pallet, index) => (
                      <td key={index} className="px-3 py-2">
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={pallet[line.id] ?? ''}
                          onChange={(event) => setQty(index, line.id, event.target.value)}
                          className="ml-auto block w-24 rounded border border-border bg-background px-2 py-1 text-right tabular-nums"
                        />
                      </td>
                    ))}
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        matches
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-red-700 dark:text-red-300'
                      }`}
                    >
                      {formatQty(total)} / {formatQty(line.qty)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!everyPalletUsed ? (
          <p className="text-xs text-red-600">{t('order.pack.empty_pallet')}</p>
        ) : !exact ? (
          <p className="text-xs text-red-600">{t('order.pack.quantities_mismatch')}</p>
        ) : null}
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded border border-border px-3 py-1.5 disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !exact || !everyPalletUsed}
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('order.pack.submit')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
